import { autoinject, bindable, Container } from 'aurelia-framework';
import { SearchResult } from 'elements/search/search-input';
import { PubSub } from 'lib/event/PubSub';
import { Keyboard } from 'lib/helpers/keyboard';
import { AsyncErrorHandler } from 'lib/ui';
import {
  MooringArticleSearchResult,
  MooringProductionGridLineDto,
  MooringProductionGridLineEditDto,
  MooringProductionGridLineSlotDto,
} from 'models/mooring';
import { MooringComponentSearchHandler, MooringService } from 'services/mooring';
import { MooringComponentSearchResult } from 'services/mooring/mooring-component-search-handler';
import { OfferViewContext } from 'views/mooring/mooring-offers/OfferViewContext';

@autoinject()
export class MooringProductionGridLinesTable {
  @bindable mooringId!: number;

  protected gridLines: MooringProductionGridLineDto[] = [];
  protected editRows: MooringProductionGridLineEditDto[] = [];

  private originalSnapshot: string | null = null;
  private context = OfferViewContext.useContext(this.container);
  private isEditing = false;

  constructor(
    private container: Container,
    private mooringService: MooringService,
    private pubsub: PubSub,
    protected mooringComponentSearchHandler: MooringComponentSearchHandler
  ) {
    this.pubsub.sub('list-entity:updated', () => {
      if (!this.isEditing) void this.getGridLines();
    });
    this.pubsub.sub('entity:updated', () => {
      if (!this.isEditing) void this.getGridLines();
    });
  }

  protected bind() {
    if (!this.mooringId) {
      throw new Error(`Mooring id is missing.`);
    }
  }

  protected async attached() {
    await this.mooringComponentSearchHandler.preCache();
    await this.getGridLines();
  }

  protected async setEdit() {
    if (this.context?.getState().isLocked) return;

    if (this.isEditing) {
      await this.getGridLines();
      this.editRows = [];
      this.originalSnapshot = null;
      this.isEditing = false;
      return;
    }

    this.editRows = this.gridLines.map(
      (l) =>
        ({
          GridLineId: l.Id,
          AnalysisNo: l.AnalysisNo,
          MblRopes: l.MblRopes,
          MblSinkRopes: l.MblSinkRopes,
          MblChainShackleConnectionPlates: l.MblChainShackleConnectionPlates,
          Length: l.Length,
          SchakleSlot: l.SchakleSlot ?? undefined,
          ThimbleSlot: l.ThimbleSlot ?? undefined,
          RopeSlot: l.RopeSlot ?? undefined,
          SpliceStrapSlot: l.SpliceStrapSlot ?? undefined,
        }) as MooringProductionGridLineEditDto
    );
    this.originalSnapshot = JSON.stringify(this.editRows);
    this.isEditing = true;
  }

  protected onArticleSelected(searchResult: MooringComponentSearchResult, slot: MooringProductionGridLineSlotDto) {
    if (!searchResult?.id || !slot) return;
    const article = searchResult.return;
    if (!article) return;
    slot.ArticleProductId = article.Id;
    slot.ProductName = article.ProductName;
  }

  @AsyncErrorHandler
  protected async save() {
    if (!this.isEditing) return;

    await this.mooringService.updateProductionGridLines(this.mooringId, this.editRows);
    this.isEditing = false
    this.editRows = [];
    this.originalSnapshot = null;
    await this.getGridLines();
  }

  @AsyncErrorHandler
  private async getGridLines() {
    const { data } = await this.mooringService.getProductionGridLines(this.mooringId);
    this.gridLines = data ?? [];
  }

  protected unbind() {
    this.pubsub.unsub();
  }

  protected detached() {
    this.pubsub.unsub();
  }

  /**
   * Handles the Enter key to navigate to the next line in the table.
   * If Shift + Enter is pressed, it navigates to the previous line.
   * field is the name of the field.
   * @param event The keyboard event.
   * @param parentIndex The index of the outer table row, not the article subtable index.
   * @param field The field name to identify the property/input.
   */
  protected tryGotoNextLine(event: KeyboardEvent, parentIndex: number, field: string) {
    console.log("parentIndex", parentIndex);

    
    const isEnterKey = Keyboard.isEnterKey(event);
    const isShiftEnter = Keyboard.isShiftEnterKey(event);

    if (!isEnterKey && !isShiftEnter) {
      return;
    }

    event.preventDefault();

    let nextIndex = isShiftEnter ? parentIndex - 1 : parentIndex + 1;
    nextIndex = Math.max(0, Math.min(this.gridLines.length - 1, nextIndex));

    const nextRow = document.querySelector(`[data-identifier="${field}-${nextIndex}"]`);

    if (!nextRow) {
      return;
    }

    nextRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
    (nextRow as HTMLInputElement | HTMLTextAreaElement | null)?.focus();
    }
}
